import React from "react";
import { Box, Dialog, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { Button } from "components/ui/Button";

function SaveStatusCard({ status, savingLabel, savedLabel }) {
  if (!status) return null;

  const isSaving = status === "saving";

  return (
    <Box
      position="fixed"
      right={{ base: "4", md: "6" }}
      bottom={{ base: "4", md: "6" }}
      zIndex="modal"
      bg="dashboard.bg.surface"
      color="dashboard.fg.default"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      rounded="2xl"
      boxShadow="0 18px 44px rgba(0, 0, 0, 0.18)"
      px="4"
      py="3"
      minW="220px"
    >
      <HStack gap="3">
        {isSaving ? (
          <Spinner size="sm" color="dashboard.accent.solid" />
        ) : (
          <Box boxSize="3" rounded="full" bg="arawaks.jungleGreen" flexShrink={0} />
        )}
        <Stack gap="0">
          <Text fontSize="sm" fontWeight="800">{isSaving ? savingLabel : savedLabel}</Text>
          <Text fontSize="xs" color="dashboard.fg.muted">
            {isSaving ? "Actualizando datos..." : "Los cambios ya estan listos."}
          </Text>
        </Stack>
      </HStack>
    </Box>
  );
}

export function FormDialog({
  open,
  onOpenChange,
  title,
  children,
  errorText,
  saving,
  onCancel,
  onSave,
  saveLabel = "Guardar",
  savingLabel = "Guardando...",
}) {
  const [status, setStatus] = React.useState(null);
  const [localSaving, setLocalSaving] = React.useState(false);
  const clearTimerRef = React.useRef(null);
  const isSaving = saving || localSaving;
  const savedLabel = `${String(title || "Formulario").replace(/^Editar\s+/i, "")} guardado`;

  React.useEffect(() => () => window.clearTimeout(clearTimerRef.current), []);

  async function handleSave() {
    if (isSaving) return;
    window.clearTimeout(clearTimerRef.current);
    setLocalSaving(true);
    setStatus("saving");

    try {
      await onSave?.();
      setStatus("saved");
      clearTimerRef.current = window.setTimeout(() => setStatus(null), 2200);
    } catch (error) {
      setStatus(null);
    } finally {
      setLocalSaving(false);
    }
  }

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange} closeOnInteractOutside={false}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            rounded="card"
            bg="dashboard.bg.dialog"
            color="dashboard.fg.default"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            overflow="hidden"
            p="0"
          >
            <Dialog.Header
              px="5"
              pt="4"
              pb="3"
              borderBottomWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <Dialog.Title fontWeight="700" fontSize="md" color="dashboard.fg.default">
                {title}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body px="5" py="5">
              <Stack gap="4">
                {children}
                {errorText ? (
                  <Box>
                    <Text color="dashboard.feedback.error" fontSize="sm" whiteSpace="pre-wrap">
                      {errorText}
                    </Text>
                  </Box>
                ) : null}
              </Stack>
            </Dialog.Body>

            <Dialog.Footer
              px="5"
              pt="3"
              pb="4"
              borderTopWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <HStack w="full" justify="flex-end" gap="2">
                <Button variant="ghost" color="gray" size="sm" onClick={onCancel} disabled={isSaving}>Cancelar</Button>
                <Button variant="surface" color="red" size="sm" onClick={handleSave} disabled={isSaving}>{isSaving ? savingLabel : saveLabel}</Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
      <SaveStatusCard status={status} savingLabel={savingLabel} savedLabel={savedLabel} />
    </>
  );
}

export default FormDialog;
